
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NewsletterModal from './NewsletterModal';

export default function ChairSection() {
  const [showCoreModal, setShowCoreModal] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const chairs = [
    {
      name: 'MeVER Chair Pro v1.1',
      price: '¥123,200',
      priceNote: '税込（Black）',
      features: [
        '4Dヘッドレスト ※画像は2Dヘッドレスト (v1.0)',
        '6Dアームレスト',
        'フルアジャスタブル設計',
      ],
      image:
        'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/18555a65f21397adfed78720e8be08d7.png?format=webp&quality=80',
      cta: 'Proを見る',
      link: '/chair/pro',
      badge: 'FLAGSHIP',
      comingSoon: false,
      headrestOption: null,
    },
    {
      name: 'MeVER Chair Core v1.0',
      price: '¥79,200',
      priceNote: '税込（Black）',
      features: ['必要十分の人間工学設計', 'ミニマルデザイン'],
      image:
        'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/f4a7d90296f789d46c6753357c7a51a3.png?format=webp&quality=80',
      cta: 'Coreを見る',
      link: '/chair/core',
      badge: 'STANDARD',
      comingSoon: true,
      headrestOption: '4Dヘッドレスト（別売りオプション）',
    },
    {
      name: 'MeVER Chair Style v1.0',
      price: '¥69,300',
      priceNote: '税込（Black）',
      features: [
        'バランスボールと似た効果の前傾姿勢機能',
        '空間と調和',
      ],
      image:
        'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/0e5886a34ba8b9935f350bf408063f8e.png?format=webp&quality=80',
      cta: 'Styleを見る',
      link: '/chair/style',
      badge: 'MINIMAL',
      comingSoon: false,
      headrestOption: null,
    },
  ];

  return (
    <>
      {/* Chair Section */}
      <section
        ref={sectionRef}
        id="chair-section"
        className="relative py-20 sm:py-28 md:py-36 bg-white overflow-hidden"
        aria-labelledby="chair-heading"
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <h2
              id="chair-heading"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-3 tracking-tight"
            >
              Sit. Focus. Flow.
            </h2>
            <p className="text-base sm:text-lg md:text-xl font-extralight text-black/50 tracking-[0.15em] mb-8 sm:mb-10">
              集中と解放
            </p>
            <a
              href="https://appointment.mever.design/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center justify-center px-8 sm:px-10 py-3 sm:py-4 bg-black text-white text-sm sm:text-base font-light tracking-[0.1em] hover:bg-black/80 transition-all whitespace-nowrap rounded-md cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2 text-base" aria-hidden="true"></i>
              MeVER Chair 体験予約
            </a>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Image */}
            <div
              className={`relative transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-neutral-100">
                <img
                  src="https://public.readdy.ai/ai/img_res/cec68db24687ff41893ef29e015fb8ba.jpg"
                  alt="MeVER Chair - 長時間作業を支えるエルゴノミクスデザイン"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  width="800"
                  height="1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
              </div>

              {/* Floating badge */}
              <div
                className={`absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-2xl p-6 transition-all duration-1000 delay-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-neutral-900">
                    <i className="ri-award-line text-xl text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-neutral-400 tracking-wider uppercase">
                      Ergonomic
                    </p>
                    <p className="text-lg font-light text-neutral-900 tracking-wide">
                      Design Award
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Chair Cards */}
            <div className="relative z-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" role="list">
                {chairs.map((chair, index) => (
                  <article key={index} className="group" role="listitem">
                    <div className="relative bg-gray-50 rounded-lg overflow-hidden mb-4 sm:mb-6 aspect-[6/7]">
                      <img
                        src={chair.image}
                        alt={`${chair.name} - 人間工学チェア`}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        width="400"
                        height="467"
                      />
                      <div className="absolute top-3 left-3 flex flex-col gap-2">
                        <span className="px-3 py-1 bg-black/80 text-white text-[10px] sm:text-xs font-medium tracking-widest rounded">
                          {chair.badge}
                        </span>
                        {chair.comingSoon && (
                          <span className="px-3 py-1 bg-[#8E7AB5] text-white text-[10px] sm:text-xs font-medium tracking-wide rounded flex items-center gap-1">
                            <i className="ri-time-line text-xs" aria-hidden="true"></i>
                            発売準備中
                          </span>
                        )}
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-light text-black mb-2 tracking-wide">
                      {chair.name}
                    </h3>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-xl sm:text-2xl font-semibold text-black tracking-tight">
                        {chair.price}
                      </span>
                      <span className="text-xs font-light text-black/40">{chair.priceNote}</span>
                    </div>
                    <ul className="space-y-1.5 mb-4" aria-label={`${chair.name}の特徴`}>
                      {chair.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="text-xs sm:text-sm font-light text-black/50 flex items-start gap-2"
                        >
                          <i className="ri-check-line text-[#8E7AB5] mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                      {chair.headrestOption && (
                        <li className="text-xs sm:text-sm font-light text-[#8E7AB5] flex items-start gap-2">
                          <i className="ri-add-circle-line text-[#8E7AB5] mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                          <span>{chair.headrestOption}</span>
                        </li>
                      )}
                    </ul>
                    <div className="flex flex-col gap-2">
                      <Link
                        to={chair.link}
                        className="inline-block text-center px-5 py-2.5 bg-[#8E7AB5] text-white text-xs sm:text-sm font-light tracking-wide hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
                        aria-label={`${chair.name}の詳細ページへ`}
                      >
                        {chair.cta}
                      </Link>
                      {chair.comingSoon ? (
                        <button
                          onClick={() => setShowCoreModal(true)}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-black/20 text-black text-xs sm:text-sm font-light tracking-wide hover:bg-black hover:text-white transition-all whitespace-nowrap rounded-md cursor-pointer"
                          aria-label="発売に関する情報を受け取る"
                        >
                          <i className="ri-mail-line text-sm" aria-hidden="true"></i>
                          発売情報を受け取る
                        </button>
                      ) : (
                        <a
                          href="https://appointment.mever.design/"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-black/20 text-black text-xs sm:text-sm font-light tracking-wide hover:bg-black hover:text-white transition-all whitespace-nowrap rounded-md cursor-pointer"
                          aria-label={`${chair.name}を体験予約する`}
                        >
                          <i className="ri-calendar-check-line text-sm" aria-hidden="true"></i>
                          体験予約
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Section - full width */}
      <section id="corporate" className="relative w-full overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/95 to-[#8E7AB5]/40"></div>

        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row items-stretch">
            {/* Left Image */}
            <div className="relative w-full lg:w-1/2 aspect-[4/3] lg:aspect-auto lg:min-h-[560px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20corporate%20office%20interior%20with%20three%20MeVER%20ergonomic%20chairs%20Core%20model%20in%20center%20Pro%20and%20Style%20models%20on%20sides%20arranged%20in%20professional%20workspace%20setting%20clean%20minimalist%20design%20natural%20lighting%20contemporary%20business%20environment%20high-end%20office%20furniture%20showcase%20professional%20photography%20sleek%20aesthetic%20productive%20workspace%20premium%20quality%20chairs%20for%20creative%20companies&width=960&height=640&seq=corporate-chairs-lineup-002&orientation=landscape"
                alt="MeVER Chair 法人向け導入 - Core、Pro、Styleモデル"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
                width="960"
                height="640"
              />
            </div>

            {/* Right Content */}
            <div className="w-full lg:w-1/2 flex items-center">
              <div className="p-10 sm:p-14 lg:p-16 xl:p-20 2xl:p-24 w-full">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full mb-6">
                  <i className="ri-building-2-line text-white/80 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                  <span className="text-xs font-light text-white/80 tracking-widest uppercase">
                    For Business
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 tracking-tight leading-tight">
                  法人向け導入<br />
                  強化中
                </h3>
                <p className="text-sm sm:text-base font-light text-white/60 leading-relaxed mb-6 sm:mb-8">
                  オフィス環境の改善で、チームの生産性とクリエイティビティを最大化。<br />
                  ボリュームディスカウント・カスタマイズ対応可能。
                </p>
                <div className="flex flex-wrap gap-3 mb-8 sm:mb-10">
                  {['コワーキング', 'スタートアップ', '制作会社', '教育機関'].map((tag) => (
                    <span
                      key={tag}
                      className="px-4 py-2 bg-white/8 border border-white/10 rounded-lg text-xs sm:text-sm font-light text-white/70 tracking-wide"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://tihhyh.readdy.co"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 bg-white text-black text-sm sm:text-base font-medium tracking-wide hover:bg-white/90 transition-all whitespace-nowrap rounded-lg cursor-pointer group"
                    aria-label="法人お問い合わせページを新しいタブで開く"
                  >
                    法人お問い合わせ
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                  </a>
                  <a
                    href="https://appointment.mever.design/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 border border-white/30 text-white text-sm sm:text-base font-light tracking-wide hover:bg-white/10 transition-all whitespace-nowrap rounded-lg cursor-pointer"
                    aria-label="法人向け体験予約"
                  >
                    <i className="ri-calendar-check-line text-base" aria-hidden="true"></i>
                    オフィス体験予約
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showCoreModal && (
        <NewsletterModal
          title="MeVER Chair Core 発売情報"
          description="発売開始時にいち早くお知らせいたします。お名前とメールアドレスをご登録ください。"
          formType="core-notify"
          formUrl="https://readdy.ai/api/form/d69vup5bd17lru29lgng"
          onClose={() => setShowCoreModal(false)}
        />
      )}
    </>
  );
}
